import {
  Button,
  Colors,
  LogoIcon,
  Modal,
  TextReg,
  useDynamicStyles,
  useMediaQueries,
} from '@sunrise-ui/primitives';
import React, { useState } from 'react';
import { Image, Linking, StyleSheet, View } from 'react-native';
import { QrCodeSvg, plainRenderer } from 'react-native-qr-svg';
import { APP_STORE_APPLE_URL, APP_STORE_GOOGLE_URL } from '../../utils/env';

type Props = {
  visible: boolean;
  hide: () => void;
};

type AppStore = 'apple' | 'google';

const APP_STORE_LINKS = {
  apple: APP_STORE_APPLE_URL,
  google: APP_STORE_GOOGLE_URL,
};

const APP_STORE_NAMES = {
  apple: 'App Store',
  google: 'Play Store',
};

export function AppDownloadQRModal({ visible, hide }: Props) {
  const { isMobile } = useMediaQueries();
  const [selectedStore, setSelectedStore] = useState<AppStore>('apple');
  const styles = useDynamicStyles(createStyles);

  const currentStoreLink = APP_STORE_LINKS[selectedStore];
  const currentStoreName = APP_STORE_NAMES[selectedStore];

  const handleDirectDownload = async () => {
    await Linking.openURL(currentStoreLink);
  };

  return (
    <Modal
      visible={visible}
      hide={hide}
      title="Download the Communiful App"
      size="medium"
      modalStyle={isMobile ? undefined : styles.modal}
    >
      <View style={styles.content}>
        <TextReg style={styles.description}>
          Scan the QR code with your phone&#39;s camera to download the app
          directly from the {currentStoreName}.
        </TextReg>

        {/* Store Toggle Buttons */}
        <View style={styles.storeToggle}>
          <Button
            mode={selectedStore === 'apple' ? 'contained' : 'text'}
            onPress={() => setSelectedStore('apple')}
            style={[
              styles.storeButton,
              selectedStore === 'apple' && styles.selectedStoreButton,
            ]}
            compact
          >
            App Store
          </Button>

          <Button
            mode={selectedStore === 'google' ? 'contained' : 'text'}
            onPress={() => setSelectedStore('google')}
            style={[
              styles.storeButton,
              selectedStore === 'google' && styles.selectedStoreButton,
            ]}
            compact
          >
            Google Play
          </Button>
        </View>

        {/* QR Code */}
        <View style={styles.qrContainer}>
          <QrCodeSvg
            value={currentStoreLink}
            frameSize={280}
            contentStyle={styles.qrContent}
            content={<Image source={LogoIcon} style={styles.icon} />}
            renderer={{ ...plainRenderer, options: { padding: 0 } }}
            backgroundColor={Colors.purple0}
            dotColor="#fff"
          />
        </View>

        {/* Alternative direct link button */}
        <Button
          mode="text"
          onPress={handleDirectDownload}
          style={styles.directLinkButton}
        >
          Open {currentStoreName} Directly
        </Button>
      </View>
    </Modal>
  );
}

const createStyles = () =>
  StyleSheet.create({
    modal: {
      maxWidth: 700,
      maxHeight: 700,
    },
    icon: {
      width: '100%',
      height: '100%',
    },
    qrContent: {
      justifyContent: 'center',
      alignItems: 'center',
      padding: 8,
    },
    container: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    content: {
      alignItems: 'center',
      maxWidth: 400,
      width: '100%',
    },
    description: {
      fontSize: 16,
      color: Colors.text,
      marginBottom: 24,
      textAlign: 'center',
      lineHeight: 22,
    },
    storeToggle: {
      flexDirection: 'row',
      gap: 12,
      marginBottom: 32,
    },
    storeButton: {
      minWidth: 100,
      paddingHorizontal: 20,
    },
    selectedStoreButton: {
      backgroundColor: Colors.purple1,
    },
    qrContainer: {
      backgroundColor: Colors.tint,
      borderRadius: 12,
      marginBottom: 24,
    },
    instructions: {
      fontSize: 14,
      color: Colors.placeholderText,
      marginBottom: 24,
      textAlign: 'center',
    },
    directLinkButton: {
      marginBottom: 16,
      borderColor: Colors.purple1,
      paddingHorizontal: 20,
    },
  });
